import { CalendarClock, FileDown, FileSearch, Flame, Landmark, MapPin, TrendingUp, X } from "lucide-react";

const formatEuro = (value) =>
  value == null ? "—" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);

const STATUS_LABELS = {
  pending_review: "À examiner",
  alerted: "Alerte envoyée",
  rejected: "Rejetée",
};

const AddressCell = ({ item, testId }) => {
  if (!item.address) {
    return (
      <span className="mandate-address-pending" data-testid={testId}>
        Adresse en cours de géocodage
      </span>
    );
  }
  return (
    <span className="mandate-address" data-testid={testId}>
      <MapPin size={13} />
      <span>{item.address}</span>
      {!item.address_exact && <em className="mandate-address-approx">approx.</em>}
    </span>
  );
};

const ScoreHistory = ({ history }) => {
  if (!history.length) return <p className="mandate-detail-muted">Historique disponible après plusieurs cycles de collecte quotidiens.</p>;
  const maxScore = Math.max(...history.map((h) => h.score), 1);
  return (
    <div className="mandate-history-bars" data-testid="commune-score-history">
      {history.slice(-14).map((entry) => (
        <div key={entry.date} title={`${entry.date} : ${entry.score}/100`}>
          <i style={{ height: `${Math.max(8, (entry.score / maxScore) * 56)}px` }} className={`mandate-history-${entry.tier}`} />
          <small>{entry.date.slice(5)}</small>
        </div>
      ))}
    </div>
  );
};

const ProspectionTable = ({ sales }) => {
  if (!sales.length) return <p className="mandate-detail-muted">Aucune vente porteuse de signal sur cette commune.</p>;
  return (
    <div className="mandate-table-wrapper">
      <table className="mandate-prospection-table" data-testid="commune-prospection-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Adresse</th>
            <th>Parcelle</th>
            <th>Prix</th>
            <th>Surface</th>
            <th>Signal</th>
          </tr>
        </thead>
        <tbody>
          {sales.map((sale, index) => (
            <tr key={`${sale.parcel_id}-${sale.mutation_date}-${index}`} data-testid={`prospection-row-${index}`}>
              <td>{sale.mutation_date}</td>
              <td>{sale.property_type || "Terrain"}</td>
              <td><AddressCell item={sale} testId={`prospection-address-${index}`} /></td>
              <td className="mandate-mono">{sale.parcel_id || "—"}</td>
              <td>{formatEuro(sale.land_value)}</td>
              <td>
                {Math.round(sale.built_surface || 0) || "—"} m²
                {sale.parcel_area_m2 ? ` · terrain ${sale.parcel_area_m2} m²` : ""}
              </td>
              <td>
                {sale.underused_land ? "Potentiel foncier" : ""}
                {sale.price_gap_percent != null && Math.abs(sale.price_gap_percent) >= 15
                  ? `${sale.underused_land ? " · " : ""}Vente atypique ${sale.price_gap_percent > 0 ? "+" : ""}${sale.price_gap_percent}%`
                  : ""}
                {!sale.underused_land && Math.abs(sale.price_gap_percent || 0) < 15 ? "Détention longue" : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const CommuneDetail = ({ detail, onClose, onExportPdf }) => {
  if (!detail) return null;
  const { sources } = detail;
  return (
    <aside className="mandate-commune-detail" data-testid="commune-detail-panel">
      <header>
        <div>
          <p className="mandate-overline">Fiche commune</p>
          <h2 data-testid="commune-detail-name">{detail.commune}</h2>
        </div>
        <button onClick={onClose} aria-label="Fermer la fiche" data-testid="commune-detail-close">
          <X size={18} />
        </button>
      </header>

      <section className="mandate-detail-sources">
        <article data-testid="commune-detail-dvf">
          <Landmark size={17} />
          <div>
            <strong>DVF · {sources.dvf.total} signal(aux)</strong>
            <small>Détail parcelle par parcelle dans le tableau de tournée ci-dessous.</small>
          </div>
        </article>
        <article data-testid="commune-detail-dpe">
          <Flame size={17} />
          <div>
            <strong>DPE · {sources.dpe.total} passoire(s) F/G</strong>
            <small>{sources.dpe.class_g} classe G · {sources.dpe.class_f} classe F · {sources.dpe.recent_24_months} récents (24 mois)</small>
          </div>
        </article>
        <article data-testid="commune-detail-bodacc">
          <FileSearch size={17} />
          <div>
            <strong>BODACC · {sources.bodacc.total} événement(s)</strong>
            {sources.bodacc.recent.slice(0, 4).map((event, index) => (
              <small key={index}>
                {event.period} · {event.legal_event === "liquidation" ? "Procédure collective" : "Vente / cession"}
                {event.entity_type ? ` · ${event.entity_type === "sci" ? "SCI immobilière" : "Particulier"}` : ""}
              </small>
            ))}
            {sources.bodacc.total === 0 && <small>Aucun événement légal récent.</small>}
          </div>
        </article>
      </section>

      <section>
        <h3><MapPin size={15} /> Tournée de prospection · adresses</h3>
        {onExportPdf && (
          <button
            className="mandate-tour-pdf-button"
            onClick={() => onExportPdf(detail.commune_code)}
            data-testid="commune-tour-pdf-button"
          >
            <FileDown size={14} /> Fiche de tournée PDF · {detail.commune}
          </button>
        )}
        <ProspectionTable sales={sources.dvf.recent} />
      </section>

      <section>
        <h3><TrendingUp size={15} /> Évolution du score</h3>
        <ScoreHistory history={detail.score_history} />
      </section>

      <section>
        <h3><CalendarClock size={15} /> Historique des opportunités</h3>
        <div className="mandate-detail-opportunities" data-testid="commune-detail-opportunities">
          {detail.opportunities.length === 0 && <p className="mandate-detail-muted">Aucune opportunité enregistrée.</p>}
          {detail.opportunities.map((opportunity) => (
            <article key={opportunity.signal_id}>
              <span className={`mandate-score mandate-score-${opportunity.tier}`}>{opportunity.opportunity_score}</span>
              <div>
                <strong>
                  {opportunity.kind === "hotspot"
                    ? "Hotspot communal"
                    : opportunity.kind === "pige"
                      ? "Annonce pige (particulier)"
                      : `Parcelle ${opportunity.parcel_id || ""}`}
                </strong>
                {opportunity.kind !== "hotspot" && (
                  <AddressCell item={opportunity} testId={`opportunity-address-${opportunity.signal_id}`} />
                )}
                <small>
                  {STATUS_LABELS[opportunity.review_status] || opportunity.review_status} · mis à jour le{" "}
                  {new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(new Date(opportunity.updated_at))}
                </small>
                <small>{opportunity.signals?.map((signal) => signal.label).join(" · ")}</small>
              </div>
            </article>
          ))}
        </div>
      </section>
    </aside>
  );
};
